import { useState, useEffect } from 'react';
import PageHeader from '../components/ui/page-header';
import { HelpButton } from '../components/ui/help-button';
import { FeedbackButton } from '../components/ui/feedback-button';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KpiStrip } from '../components/ui/kpi-strip';
import { RightPanel } from '../components/ui/right-panel';
import { ChainVisual } from '../components/ui/chain-visual';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  AlertTriangle,
  Search,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Cpu,
  ClipboardList,
  FileSearch,
  ArrowRight,
  ArrowUpDown,
  ChevronRight,
  UserPlus,
  PlayCircle,
  ShieldCheck
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { ContextualTip } from '../components/ContextualTip';
import DecisionRow from '../components/DecisionRow';
import { PageError } from '../components/PageError';
import { Link, useNavigate } from 'react-router-dom';

import { PageToolbar, PageShell } from '../components/enterprise';

const VulnerabilitiesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, resolved: 0, avg_age_days: 0 });
  const [ownerRoles, setOwnerRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [hasAssessments, setHasAssessments] = useState(true);
  
  // Detail panel
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [currentVuln, setCurrentVuln] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [assets, setAssets] = useState([]);

  const toggleSort = (field) => {
    if (sortField === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  useEffect(() => {
    fetchData();
    checkAssessments();
    fetchAssets();
  }, [statusFilter, domainFilter, severityFilter]);

  const fetchAssets = async () => {
    try {
      const res = await api.get('/ggrc/customer-assets');
      setAssets(res.data.assets || []);
    } catch {
      // Assets not critical for this page
    }
  };

  const checkAssessments = async () => {
    try {
      const response = await api.get('/assessments/history');
      setHasAssessments((response.data?.total || 0) > 0 || (response.data?.snapshots?.length || 0) > 0);
    } catch (error) {
      setHasAssessments(false);
    }
  };

  const fetchData = async () => {
    setFetchError(false);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (domainFilter !== 'all') params.append('domain', domainFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      
      const [vulnRes, rolesRes] = await Promise.all([
        api.get(`/ggrc/vulnerabilities?${params.toString()}`),
        api.get('/ggrc/owner-roles')
      ]);
      
      setVulnerabilities(vulnRes.data.vulnerabilities || []);
      setStats(vulnRes.data.stats || { total: 0, open: 0, resolved: 0, avg_age_days: 0 });
      setOwnerRoles(rolesRes.data || []);
    } catch (error) {
      setFetchError(true);
      toast.error('Zwakke plekken laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVuln = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/ggrc/vulnerabilities/${currentVuln.id}`, {
        status: currentVuln.status,
        owner_id: currentVuln.owner_id,
        severity: currentVuln.severity,
        asset_id: currentVuln.asset_id || null,
        classification: currentVuln.classification || null
      });
      toast.success('Zwakke plek bijgewerkt');
      setDetailPanelOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Zwakke plek bijwerken mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      low: 'border-transparent bg-green-50 text-green-700',
      medium: 'border-transparent bg-yellow-50 text-yellow-700',
      high: 'border-transparent bg-orange-50 text-orange-700',
      critical: 'border-transparent bg-red-50 text-red-700'
    };
    const labels = { low: 'Laag', medium: 'Midden', high: 'Hoog', critical: 'Kritiek' };
    return <Badge className={variants[severity] || variants.medium}>{labels[severity] || severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'border-transparent bg-red-50 text-red-700',
      in_progress: 'border-transparent bg-yellow-50 text-yellow-700',
      resolved: 'border-transparent bg-green-50 text-green-700'
    };
    const labels = { open: 'Open', in_progress: 'In behandeling', resolved: 'Opgelost' };
    return <Badge className={variants[status] || variants.open}>{labels[status] || status}</Badge>;
  };

  const getDomainIcon = (domain) => {
    switch (domain) {
      case 'technisch': case 'technology': return <Cpu className="h-4 w-4 text-blue-500" strokeWidth={1.5} />;
      case 'proces': case 'process': return <ClipboardList className="h-4 w-4 text-purple-500" strokeWidth={1.5} />;
      case 'mens': case 'people': return <Users className="h-4 w-4 text-orange-500" strokeWidth={1.5} />;
      default: return <Cpu className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />;
    }
  };

  const getDomainLabel = (domain) => {
    const labels = { technisch: 'Technisch', technology: 'Technisch', proces: 'Proces', process: 'Proces', mens: 'Mens', people: 'Mens' };
    return labels[domain] || domain;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Count by severity for left widget
  const severityCounts = {
    critical: vulnerabilities.filter(v => v.severity === 'critical').length,
    high: vulnerabilities.filter(v => v.severity === 'high').length,
    medium: vulnerabilities.filter(v => v.severity === 'medium').length,
    low: vulnerabilities.filter(v => v.severity === 'low').length
  };

  // Count by domain for right widget
  const domainCounts = {
    technisch: vulnerabilities.filter(v => v.domain === 'technisch').length,
    proces: vulnerabilities.filter(v => v.domain === 'proces').length,
    mens: vulnerabilities.filter(v => v.domain === 'mens').length
  };

  // Determine next step for a vulnerability
  const getVulnNextStep = (vuln) => {
    const isHighSev = vuln.severity === 'high' || vuln.severity === 'critical';
    const hasOwner = !!vuln.owner_id;

    if (vuln.status === 'resolved') {
      return { text: 'Opgelost', icon: CheckCircle, color: 'text-green-600 bg-green-50', action: null };
    }
    if (vuln.status === 'open' && !hasOwner && isHighSev) {
      return { text: 'Wijs eigenaar toe', icon: UserPlus, color: 'text-red-600 bg-red-50', action: 'assign' };
    }
    if (vuln.status === 'open' && !hasOwner) {
      return { text: 'Wijs eigenaar toe', icon: UserPlus, color: 'text-amber-600 bg-amber-50', action: 'assign' };
    }
    if (vuln.status === 'open' && hasOwner) {
      return { text: 'Start behandeling', icon: PlayCircle, color: 'text-blue-600 bg-blue-50', action: 'progress' };
    }
    if (vuln.status === 'in_progress') {
      return { text: 'Markeer opgelost', icon: ShieldCheck, color: 'text-green-600 bg-green-50', action: 'resolve' };
    }
    return { text: '-', icon: Clock, color: 'text-neutral-400 bg-neutral-50', action: null };
  };

  const handleQuickAction = async (vuln, action) => {
    try {
      const updates = {};
      if (action === 'progress') updates.status = 'in_progress';
      if (action === 'resolve') updates.status = 'resolved';
      if (action === 'assign') {
        setCurrentVuln({ ...vuln });
        setDetailPanelOpen(true);
        return;
      }
      if (Object.keys(updates).length > 0) {
        await api.patch(`/ggrc/vulnerabilities/${vuln.id}`, updates);
        toast.success('Zwakke plek bijgewerkt');
        fetchData();
      }
    } catch {
      toast.error('Bijwerken mislukt');
    }
  };

  // Apply search filter
  const filteredVulns = vulnerabilities.filter(v =>
    v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.vuln_id?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    if (!sortField) return 0;
    const valA = a[sortField] || '';
    const valB = b[sortField] || '';
    const cmp = typeof valA === 'number' ? valA - valB : String(valA).localeCompare(String(valB));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (loading) {
    return <PageShell section="risicos" testId="vulnerabilities-page" loading />;
  }

  if (fetchError) {
    return (
      <PageShell section="risicos" testId="vulnerabilities-page">
        <div className="px-6 py-10">
          <PageError onRetry={() => { setLoading(true); fetchData(); }} />
        </div>
      </PageShell>
    );
  }

  // Human-readable helpers
  const getVulnDescription = (vuln) => {
    const sevMap = { critical: 'Dit is een ernstig probleem', high: 'Dit verdient snel aandacht', medium: 'Dit is een aandachtspunt', low: 'Dit is een klein aandachtspunt' };
    const statusMap = { open: 'en is nog niet opgepakt', in_progress: 'en wordt behandeld', resolved: 'en is opgelost' };
    return `${sevMap[vuln.severity] || 'Aandachtspunt'} ${statusMap[vuln.status] || ''}`;
  };

  const getVulnSLA = (severity) => {
    const slaMap = { critical: 'Oplossen binnen 48 uur', high: 'Oplossen binnen 7 dagen', medium: 'Oplossen binnen 30 dagen', low: 'Oplossen binnen 90 dagen' };
    return slaMap[severity] || '';
  };

  const criticalOpen = vulnerabilities.filter(v => (v.severity === 'critical' || v.severity === 'high') && v.status === 'open').length;

  return (
    <PageShell section="risicos" testId="vulnerabilities-page">
      <PageToolbar
        title="Kwetsbaarheden"
        subtitle={vulnerabilities.length === 0 ? 'Nog geen zwakke plekken gevonden' : `${vulnerabilities.length} zwakke plekken, ${criticalOpen} hoog/kritiek open`}
        count={vulnerabilities.length > 0 ? vulnerabilities.length : undefined}
        testId="vulnerabilities"
      >
        {vulnerabilities.length === 0 && (
          <Button onClick={() => navigate('/assessment')} className="h-10 text-sm rounded-lg bg-[#111827] text-white hover:bg-neutral-800" data-testid="vuln-start-assessment">
            Start nulmeting <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
          </Button>
        )}
      </PageToolbar>

      <div className="px-6 py-6 space-y-6">
                {/* SUMMARY ROW */}
                {vulnerabilities.length > 0 && (
                  <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vuln-summary-row">
                    {/* Severity donut */}
                    <div className="rounded-xl border border-neutral-200 p-4 flex flex-col items-center justify-center" data-testid="vuln-donut">
                      <VulnDonut counts={severityCounts} total={vulnerabilities.length} onFilter={setSeverityFilter} active={severityFilter} />
                      <p className="text-[11px] text-neutral-400 mt-2">Klik om te filteren</p>
                    </div>
                    {/* Quick stats */}
                    <div className="rounded-xl border border-neutral-200 p-4 space-y-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Overzicht</p>
                      {[
                        { label: 'Open', value: stats.open, color: stats.open > 0 ? 'text-red-600' : 'text-neutral-600' },
                        { label: 'In behandeling', value: vulnerabilities.filter(v => v.status === 'in_progress').length, color: 'text-amber-600' },
                        { label: 'Opgelost', value: stats.resolved, color: 'text-emerald-600' },
                        { label: 'Gem. leeftijd', value: `${stats.avg_age_days || 0}d`, color: 'text-neutral-600' },
                      ].map((s, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-xs text-neutral-500">{s.label}</span>
                          <span className={`text-sm font-bold ${s.color}`}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                    {/* Opgelost percentage */}
                    <div className="rounded-xl border border-neutral-200 p-4 flex flex-col justify-center">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mb-2">Opgelost</p>
                      <p className="text-3xl font-bold text-emerald-600">
                        {vulnerabilities.length > 0 ? Math.round((stats.resolved / vulnerabilities.length) * 100) : 0}%
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">{stats.resolved} van {vulnerabilities.length} opgelost</p>
                      <div className="w-full bg-neutral-100 rounded-full h-1.5 mt-3">
                        <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${vulnerabilities.length > 0 ? (stats.resolved / vulnerabilities.length) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </section>
                )}

                {/* FILTERS */}
                {vulnerabilities.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-neutral-100" data-testid="vuln-filters">
                    <div className="relative flex-1 min-w-[320px] max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                      <input type="text" placeholder="Zoek op naam of CVE..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-10 pl-10 pr-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black/10" data-testid="vuln-search-input" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-36 h-10 text-sm border-neutral-200 rounded-lg"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle statussen</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In behandeling</SelectItem>
                        <SelectItem value="resolved">Opgelost</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={severityFilter} onValueChange={setSeverityFilter}>
                      <SelectTrigger className="w-32 h-10 text-sm border-neutral-200 rounded-lg"><SelectValue placeholder="Alle ernst" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle</SelectItem>
                        <SelectItem value="low">Laag</SelectItem>
                        <SelectItem value="medium">Midden</SelectItem>
                        <SelectItem value="high">Hoog</SelectItem>
                        <SelectItem value="critical">Kritiek</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={domainFilter} onValueChange={setDomainFilter}>
                      <SelectTrigger className="w-32 h-10 text-sm border-neutral-200 rounded-lg"><SelectValue placeholder="Alle domeinen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle domeinen</SelectItem>
                        <SelectItem value="technisch">Technisch</SelectItem>
                        <SelectItem value="proces">Proces</SelectItem>
                        <SelectItem value="mens">Mens</SelectItem>
                      </SelectContent>
                    </Select>
                    {filteredVulns.length !== vulnerabilities.length && (
                      <span className="text-xs text-neutral-400 ml-auto">{filteredVulns.length} resultaten</span>
                    )}
                  </div>
                )}

                {/* COMPACT TABLE */}
                {filteredVulns.length > 0 && (
                  <section className="rounded-xl border border-neutral-200 overflow-hidden" data-testid="vuln-table-section">
                    <div className="overflow-x-auto">
                      <table className="spk-table" data-testid="vuln-table">
                        <thead>
                          <tr>
                            <th className="cursor-pointer select-none" onClick={() => toggleSort('title')}>
                              Kwetsbaarheid {sortField === 'title' && <ArrowUpDown className="h-3 w-3 inline ml-1" />}
                            </th>
                            <th className="w-20">Domein</th>
                            <th className="w-24">Ernst</th>
                            <th className="w-28">Status</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredVulns.map((vuln) => {
                            const isSelected = currentVuln?.id === vuln.id && detailPanelOpen;
                            return (
                              <tr key={vuln.id}
                                className={`cursor-pointer ${isSelected ? '!bg-neutral-100' : ''}`}
                                onClick={() => { setCurrentVuln({ ...vuln }); setDetailPanelOpen(true); }}
                                data-testid={`vuln-row-${vuln.vuln_id}`}>
                                <td>
                                  <p className="text-sm font-medium text-black truncate max-w-[280px]">{vuln.title}</p>
                                  {vuln.asset_name && <p className="text-[10px] text-neutral-400 mt-0.5">{vuln.asset_name}</p>}
                                </td>
                                <td><span className="text-[10px] text-neutral-500">{getDomainLabel(vuln.domain)}</span></td>
                                <td>{getSeverityBadge(vuln.severity)}</td>
                                <td>{getStatusBadge(vuln.status)}</td>
                                <td><ChevronRight className="h-3.5 w-3.5 text-neutral-300" strokeWidth={1.5} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* EMPTY STATE */}
                {vulnerabilities.length === 0 && (
                  <section className="rounded-xl border-2 border-dashed border-neutral-300 p-10 text-center" data-testid="empty-vulns">
                    <AlertTriangle className="h-8 w-8 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                    <h2 className="text-lg font-semibold text-black">Geen kwetsbaarheden</h2>
                    <p className="text-sm text-neutral-500 mt-1 max-w-sm mx-auto">Start een nulmeting om zwakke plekken te ontdekken.</p>
                  </section>
                )}
              </div>

          {/* RIGHT: Detail Split Panel */}
          {detailPanelOpen && currentVuln && (
            <div className="w-[440px] shrink-0 border-l border-neutral-200 bg-white h-screen sticky top-0 overflow-y-auto" data-testid="vuln-detail-split">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-neutral-200">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono text-neutral-400">{currentVuln.vuln_id}</span>
                      {getSeverityBadge(currentVuln.severity)}
                    </div>
                    <h2 className="text-sm font-bold text-black">{currentVuln.title}</h2>
                  </div>
                  <button onClick={() => { setDetailPanelOpen(false); setCurrentVuln(null); }} className="ml-3 h-7 w-7 rounded-lg border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 shrink-0" data-testid="vuln-split-close">
                    <XCircle className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                  {/* Key attributes */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-neutral-50">
                      <p className="text-[9px] font-bold uppercase text-neutral-400 mb-0.5">Domein</p>
                      <div className="flex items-center gap-1.5">
                        {getDomainIcon(currentVuln.domain)}
                        <span className="text-xs font-medium">{getDomainLabel(currentVuln.domain)}</span>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-neutral-50">
                      <p className="text-[9px] font-bold uppercase text-neutral-400 mb-0.5">CVSS</p>
                      <span className="text-lg font-bold">{currentVuln.cvss ? currentVuln.cvss.toFixed(1) : '-'}</span>
                    </div>
                    <div className="p-3 rounded-lg bg-neutral-50">
                      <p className="text-[9px] font-bold uppercase text-neutral-400 mb-0.5">CVE</p>
                      <span className="text-xs font-mono">{currentVuln.cve_id || '-'}</span>
                    </div>
                    <div className="p-3 rounded-lg bg-neutral-50">
                      <p className="text-[9px] font-bold uppercase text-neutral-400 mb-0.5">Ontdekt</p>
                      <span className="text-xs">{formatDate(currentVuln.created_at)}</span>
                    </div>
                  </div>

                  {currentVuln.description && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Beschrijving</p>
                      <p className="text-xs text-neutral-600 leading-relaxed">{currentVuln.description}</p>
                    </div>
                  )}

                  {/* SLA indicator */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs bg-amber-50 border-amber-200 text-amber-700">
                    <Clock className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                    <span className="font-medium">{getVulnSLA(currentVuln.severity)}</span>
                  </div>

                  {/* Editable fields */}
                  <div className="space-y-3 pt-3 border-t border-neutral-100">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Wijzigen</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Status</Label>
                        <Select value={currentVuln.status} onValueChange={(v) => setCurrentVuln(c => ({ ...c, status: v }))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In behandeling</SelectItem>
                            <SelectItem value="resolved">Opgelost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Ernst</Label>
                        <Select value={currentVuln.severity} onValueChange={(v) => setCurrentVuln(c => ({ ...c, severity: v }))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Laag</SelectItem>
                            <SelectItem value="medium">Midden</SelectItem>
                            <SelectItem value="high">Hoog</SelectItem>
                            <SelectItem value="critical">Kritiek</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Eigenaar</Label>
                        <Select value={currentVuln.owner_id || ''} onValueChange={(v) => setCurrentVuln(c => ({ ...c, owner_id: v }))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selecteer" /></SelectTrigger>
                          <SelectContent>
                            {ownerRoles.map(role => <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-neutral-500">Asset</Label>
                        <Select value={currentVuln.asset_id || 'none'} onValueChange={(v) => setCurrentVuln(c => ({ ...c, asset_id: v === 'none' ? null : v }))}>
                          <SelectTrigger className="h-8 text-xs" data-testid="vuln-asset-select"><SelectValue placeholder="Selecteer" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Geen</SelectItem>
                            {assets.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-neutral-200 flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="text-xs text-neutral-400 h-8 px-2" onClick={() => { setDetailPanelOpen(false); setCurrentVuln(null); }}>Annuleren</Button>
                  <div className="flex-1" />
                  <Button size="sm" className="bg-[#111827] text-white text-xs h-8 px-3 rounded-lg" onClick={handleUpdateVuln} disabled={actionLoading} data-testid="vuln-save-button">
                    {actionLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Opslaan
                  </Button>
                </div>
              </div>
            </div>
          )}
    </PageShell>
  );
};

// Mini donut for vulnerabilities
const VulnDonut = ({ counts, total, onFilter, active }) => {
  const levels = [
    { key: 'critical', label: 'Kritiek', color: '#dc2626', count: counts.critical },
    { key: 'high', label: 'Hoog', color: '#f97316', count: counts.high },
    { key: 'medium', label: 'Midden', color: '#eab308', count: counts.medium },
    { key: 'low', label: 'Laag', color: '#22c55e', count: counts.low },
  ].filter(l => l.count > 0);
  const size = 120; const sw = 16; const r = (size - sw) / 2; const circ = 2 * Math.PI * r;
  let off = 0;
  const segs = levels.map(l => { const d = circ * (l.count / total); const s = { ...l, dash: d, offset: off }; off += d; return s; });
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f5f5f5" strokeWidth={sw} />
        {segs.map((s, i) => (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={s.color}
            strokeWidth={active === s.key ? sw + 4 : sw}
            strokeDasharray={`${s.dash} ${circ - s.dash}`} strokeDashoffset={-s.offset}
            className="cursor-pointer transition-all hover:opacity-80"
            onClick={() => onFilter(active === s.key ? 'all' : s.key)} />
        ))}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-black">{total}</span>
        <span className="text-[9px] text-neutral-400">totaal</span>
      </div>
    </div>
  );
};

export default VulnerabilitiesPage;
